import {UseFormReturn} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {Grid} from "@mui/material";
import {LabeledInput} from "../base/LabeledInput";
import {COUNTRY_OPTIONS} from "../../utils/countriesUtils";
import {TextInput} from "../base/TextInput";
import {AutocompleteInput} from "../base/AutocompleteInput";
import {Company} from "../../types/domain/company";
import {CheckboxInput} from "../base/CheckboxInput";

export interface CompanyInfoFormInputs {
    name: string;
    parentCompany: string | null;
    vat: string;
    invoiceEmail: string;
    address: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
    phone: string;
    hasNoAddress: boolean;
}

export interface Props {
    initFormValues: CompanyInfoFormInputs;
    form: UseFormReturn<CompanyInfoFormInputs>;
    companies: Company[];
    isCompaniesLoading: boolean;
    enableParentCompanySelection: boolean;
}

export const CompanyInfoForm: React.FC<Props> = ({
    initFormValues,
    form: {
        register,
        control,
        formState: {errors},
        watch,
    },
    companies,
    isCompaniesLoading,
    enableParentCompanySelection,
}) => {
    const {t} = useTranslation();
    const hasNoAddress = watch("hasNoAddress");

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <LabeledInput label={t("companies.info_form.name")}>
                    <TextInput name="name" type="text" register={register} errors={errors} />
                </LabeledInput>
            </Grid>
            <Grid item xs={12}>
                <LabeledInput label={t("companies.info_form.parent")}>
                    <AutocompleteInput
                        name="parentCompany"
                        control={control}
                        options={companies}
                        isLoading={isCompaniesLoading}
                        initialValue={initFormValues.country}
                        getOptionLabel={(option) => option.properties.name}
                        disabled={!enableParentCompanySelection}
                    />
                </LabeledInput>
            </Grid>
            <Grid item xs={12}>
                <CheckboxInput
                    name="hasNoAddress"
                    control={control}
                    initialValue={initFormValues.hasNoAddress}
                    label={t("companies.info_form.no_address")}
                    inputProps={{sx: {marginTop: "0.25rem"}}}
                />
            </Grid>
            <Grid item xs={12}>
                <LabeledInput label={t("companies.info_form.street")} disabled={hasNoAddress}>
                    <TextInput name="address" type="text" register={register} errors={errors} disabled={hasNoAddress} />
                </LabeledInput>
            </Grid>
            <Grid item xs={6}>
                <LabeledInput label={t("companies.info_form.city")} disabled={hasNoAddress}>
                    <TextInput name="city" type="text" register={register} errors={errors} disabled={hasNoAddress} />
                </LabeledInput>
            </Grid>
            <Grid item xs={6}>
                <LabeledInput label={t("companies.info_form.postalcode")} disabled={hasNoAddress}>
                    <TextInput
                        name="postalCode"
                        type="text"
                        register={register}
                        errors={errors}
                        disabled={hasNoAddress}
                    />
                </LabeledInput>
            </Grid>
            <Grid item xs={6}>
                <LabeledInput label={t("companies.info_form.country")} disabled={hasNoAddress}>
                    <AutocompleteInput
                        name="country"
                        control={control}
                        options={COUNTRY_OPTIONS}
                        disableClear
                        initialValue={initFormValues.country}
                        getOptionLabel={(option) => option.label}
                        disabled={hasNoAddress}
                    />
                </LabeledInput>
            </Grid>
            <Grid item xs={6}>
                <LabeledInput label={t("companies.info_form.state")} disabled={hasNoAddress}>
                    <TextInput name="state" type="text" register={register} errors={errors} disabled={hasNoAddress} />
                </LabeledInput>
            </Grid>
            <Grid item xs={6}>
                <LabeledInput label={t("companies.info_form.phone")}>
                    <TextInput name="phone" type="text" register={register} errors={errors} />
                </LabeledInput>
            </Grid>
            <Grid item xs={6}>
                <LabeledInput label={t("companies.info_form.vat")}>
                    <TextInput name="vat" type="text" register={register} errors={errors} />
                </LabeledInput>
            </Grid>
            <Grid item xs={12}>
                <LabeledInput label={t("companies.info_form.invoice_email")}>
                    <TextInput name="invoiceEmail" type="text" register={register} errors={errors} />
                </LabeledInput>
            </Grid>
        </Grid>
    );
};
